import { useState, useRef, useEffect } from "react";
import { ALL_SPORTS } from "@/lib/sports";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { Check, Dribbble, X } from "lucide-react";
import { cn } from "@/lib/utils";

export default function SportAutocomplete({ value, onValueChange, placeholder = "Search sports...", className = "", triggerClassName = "" }) {
  const [open, setOpen] = useState(false);
  const selected = ALL_SPORTS.find(s => s.value === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid="sport-autocomplete-trigger" type="button"
          className={cn(
            "flex h-11 w-full items-center justify-between border-2 border-slate-200 bg-transparent px-3 py-2 text-sm font-medium transition-colors hover:border-slate-300 focus:border-court-orange focus:outline-none",
            triggerClassName
          )}>
          <span className="flex items-center gap-2">
            {selected ? (
              <>
                <selected.icon className="w-4 h-4 text-court-orange shrink-0" />
                <span>{selected.label}</span>
              </>
            ) : (
              <>
                <Dribbble className="w-4 h-4 text-court-orange shrink-0" />
                <span className="text-muted-foreground">{placeholder}</span>
              </>
            )}
          </span>
          {value ? (
            <span onClick={(e) => { e.stopPropagation(); onValueChange(""); }} className="text-muted-foreground hover:text-foreground cursor-pointer p-0.5">
              <X className="w-3.5 h-3.5" />
            </span>
          ) : (
            <svg className="w-4 h-4 text-muted-foreground shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start" data-testid="sport-autocomplete-list">
        <Command>
          <CommandInput placeholder="Type to search..." className="h-10 text-sm" data-testid="sport-autocomplete-input" />
          <CommandList className="max-h-[240px]">
            <CommandEmpty className="py-4 text-center text-sm text-muted-foreground">No sport found</CommandEmpty>
            <CommandGroup>
              {ALL_SPORTS.map(sport => {
                const Icon = sport.icon;
                return (
                  <CommandItem key={sport.value} value={sport.label}
                    data-testid={`sport-option-${sport.value}`}
                    onSelect={() => { onValueChange(sport.value); setOpen(false); }}
                    className="flex items-center gap-2 px-3 py-2.5 cursor-pointer text-sm">
                    <Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                    <span className="flex-1">{sport.label}</span>
                    {value === sport.value && <Check className="w-4 h-4 text-court-orange shrink-0" />}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
